/**
 * 3D Planet — kleurwissel live (V2).
 * Kiest de klant een kleur-swatch (optie "Kleur"), dan krijgt elk `model-viewer`-model op de pagina
 * (Shopify's productgalerij, ook de zoom-dialoog) die filamentkleur als baseColor. Hex-waarden komen
 * uit het metaobject material.colors (JSON in het blok), match op de naam van de optiewaarde.
 * Publiceert ook `tdp-3d:color` op document, zodat de personalisatie-viewer dezelfde kleur volgt.
 *
 * Gebruikt de model-viewer scene-graph-API:
 *   modelViewer.model.materials[i].pbrMetallicRoughness.setBaseColorFactor([r, g, b, a])  (lineaire RGB)
 */
const SKIP_MATERIAL = /silver|metal|ring/i; // metalen onderdelen (AMS-ring) houden hun eigen kleur
const EVENT_NAME = 'tdp-3d:color';

/** @param {string} hex */
export function hexToLinearRgba(hex) {
  const clean = String(hex || '').trim().replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  const channel = (index) => {
    const c = parseInt(full.slice(index, index + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return [channel(0), channel(2), channel(4), 1];
}

/** @param {Record<string, string>} colors @param {string} name */
export function findHex(colors, name) {
  const wanted = String(name || '').trim().toLowerCase();
  const key = Object.keys(colors).find((k) => k.trim().toLowerCase() === wanted);
  return key ? colors[key] : null;
}

class TdpColorSync extends HTMLElement {
  #colors = {};
  #optionName = 'Kleur';
  #observer = null;
  #current = null;
  #abort = new AbortController();

  connectedCallback() {
    this.#optionName = this.dataset.optionName || 'Kleur';
    try {
      this.#colors = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch (error) {
      console.warn('[tdp-3d-color-sync] kleurenlijst onleesbaar', error);
      this.#colors = {};
    }
    const { signal } = this.#abort;
    document.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.#observer = new MutationObserver(() => this.#scanViewers());
    this.#observer.observe(document.body, { childList: true, subtree: true });
    this.#current = this.selectedColorName();
    this.#scanViewers();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abort.abort();
  }

  /** Naam van de nu gekozen kleur (radio-swatches of dropdown). */
  selectedColorName() {
    const radio = document.querySelector(`input[type="radio"][data-option-name="${CSS.escape(this.#optionName)}"]:checked`);
    if (radio) return radio.value;
    const select = document.querySelector(`select[name="options[${this.#optionName}]"]`);
    return select ? select.value : null;
  }

  /** @param {Event} event */
  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target instanceof HTMLInputElement && target.dataset.optionName !== this.#optionName) return;
    if (target instanceof HTMLSelectElement && target.name !== `options[${this.#optionName}]`) return;
    this.#current = target.value;
    this.#applyAll();
  }

  #scanViewers() {
    document.querySelectorAll('model-viewer').forEach((viewer) => this.#attach(viewer));
  }

  /** @param {Element} viewer */
  #attach(viewer) {
    if (viewer.hasAttribute('data-tdp-color-sync')) return;
    viewer.setAttribute('data-tdp-color-sync', '');
    viewer.addEventListener('load', () => this.#apply(viewer), { signal: this.#abort.signal });
    if (viewer.loaded || viewer.model) this.#apply(viewer);
  }

  #applyAll() {
    document.querySelectorAll('model-viewer').forEach((viewer) => this.#apply(viewer));
    const hex = findHex(this.#colors, this.#current);
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { name: this.#current, hex } }));
  }

  /** @param {any} viewer */
  #apply(viewer) {
    const hex = findHex(this.#colors, this.#current);
    const rgba = hex ? hexToLinearRgba(hex) : null;
    const materials = viewer?.model?.materials;
    if (!rgba || !materials) return;
    try {
      materials.forEach((material) => {
        if (SKIP_MATERIAL.test(material.name || '')) return;
        material.pbrMetallicRoughness.setBaseColorFactor(rgba);
      });
    } catch (error) {
      console.warn('[tdp-3d-color-sync] kleur niet toegepast', error);
    }
  }
}

if (!customElements.get('tdp-3d-color-sync')) {
  customElements.define('tdp-3d-color-sync', TdpColorSync);
}
